import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import {
  SearchConditionParams,
  SearchType,
} from 'components/SearchSideBar/types';
import { removeUndefinedFromObject } from '@doda/common/src/utils/objectUtil';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { createConditionQuery } from 'logics/createConditionQuery';
import { getKeys } from '../tools';
import { DEFAULT_PARAMS, LOGIN_STATUS, SEARCH_TYPE } from '../../constants';
import { createOccupationUrlQuery } from '../createQuery/occupationQuery';
import { createUsrclkConditionQuery } from '../usrclkConditionQuery';
import { createOpQuery } from '../createQuery/details';
import { createIncomeQuery } from '../createQuery/incomeQuery';
import { createKeywordQuery } from '../createQuery/keywordQuery';
import { createIndustryQuery } from '../createQuery/industryQuery';
import { createJobOfferKindQuery } from '../createQuery/details/jobOfferKind';

interface ArgumentType {
  selectedCurrentCondition?: SelectedCurrentConditionType;
  qc?: string | undefined;
  mpsc_dtno?: string | undefined;
  oldestDayWdtno?: string | undefined;
  leftPanelType?: string | undefined;
  usrclk?: string | undefined;
  defaultParams?: Partial<typeof DEFAULT_PARAMS>;
  hasPf?: boolean;
  hasSo?: boolean;
}

/**
 * 選択中の条件からクエリのオブジェクトを生成
 * @param selectedCurrentCondition
 * @returns {
 *  op: '01L,02L',
 *  ar: '2,3'
 * }
 */
export const createConditionQueryObject = ({
  selectedCurrentCondition,
  qc,
  mpsc_dtno,
  oldestDayWdtno,
  leftPanelType,
  usrclk,
  defaultParams,
  hasPf = false,
  hasSo = false,
}: ArgumentType): SearchConditionParams => {
  // 各条件のクエリを生成
  const occupationQuery = createOccupationUrlQuery(
    selectedCurrentCondition?.searchJobTypeLId ?? [],
    selectedCurrentCondition?.searchJobTypeMId ?? [],
    selectedCurrentCondition?.searchJobTypeSId ?? []
  );

  const areaQuery = createConditionQuery(
    selectedCurrentCondition?.areaId ?? [],
    'ar'
  );
  const prefectureQuery = createConditionQuery(
    selectedCurrentCondition?.prefectureId ?? [],
    'pr'
  );
  const cityQuery = createConditionQuery(
    selectedCurrentCondition?.cityId ?? [],
    'ci'
  );
  const wardQuery = createConditionQuery(
    selectedCurrentCondition?.wardId ?? [],
    'wa'
  );
  const routeQuery = createConditionQuery(
    selectedCurrentCondition?.routeId ?? [],
    'rt'
  );
  const stationQuery = createConditionQuery(
    selectedCurrentCondition?.stationId ?? [],
    'st'
  );
  const industryQuery = createIndustryQuery(
    selectedCurrentCondition?.industryLId ?? [],
    selectedCurrentCondition?.industrySId ?? []
  );
  const optionQuery = createOpQuery({
    employmentStatus: selectedCurrentCondition?.employmentStatus ?? [],
    isNewArrival: selectedCurrentCondition?.isNewArrival ?? false,
    isNearDeadline: selectedCurrentCondition?.isNearDeadline ?? false,
    holidayAndWayOfWorking:
      selectedCurrentCondition?.holidayAndWayOfWorking ?? [],
    requirements: selectedCurrentCondition?.requirements ?? [],
    workplaceEnvironment: selectedCurrentCondition?.workplaceEnvironment ?? [],
    welfareBenefits: selectedCurrentCondition?.welfareBenefits ?? [],
    languageSkill: selectedCurrentCondition?.languageSkills ?? [],
    positions: selectedCurrentCondition?.positions ?? [],
    employeeAverageAge: selectedCurrentCondition?.employeeAverageAge ?? [],
  });

  const incomeQuery = createIncomeQuery(
    selectedCurrentCondition?.lowerLimitIncome,
    selectedCurrentCondition?.upperLimitIncome
  );

  const includeKeywordQuery = createKeywordQuery(
    selectedCurrentCondition?.includedKeyword ?? [],
    selectedCurrentCondition?.includedKeywordCategory,
    true
  );

  const excludeKeywordQuery = createKeywordQuery(
    selectedCurrentCondition?.excludedKeyword ?? [],
    selectedCurrentCondition?.excludedKeywordCategory,
    false
  );

  const employNumQuery = createConditionQuery(
    selectedCurrentCondition?.employNum ?? [],
    'ne'
  );

  const establishQuery = createConditionQuery(
    selectedCurrentCondition?.establish ?? [],
    'es'
  );

  const jobOfferKindQuery = createJobOfferKindQuery(
    selectedCurrentCondition?.jobOfferKind,
    defaultParams?.tp
  );

  return {
    qc: qc ? `qc=${qc}` : undefined,
    mpsc_dtno: mpsc_dtno ? `mpsc_dtno=${mpsc_dtno}` : undefined,
    k: includeKeywordQuery.keyword,
    kwc: includeKeywordQuery.category,
    ni: excludeKeywordQuery.keyword,
    niwc: excludeKeywordQuery.category,
    ss: defaultParams?.ss ? `ss=${defaultParams.ss}` : undefined,
    op: optionQuery,
    pf: hasPf ? 'pf=1' : undefined,
    pic: defaultParams?.pic ? `pic=${defaultParams.pic}` : undefined,
    ds: defaultParams?.ds ? `ds=${defaultParams.ds}` : undefined,
    oc: occupationQuery,
    ar: areaQuery,
    pr: prefectureQuery,
    ci: cityQuery,
    wa: wardQuery,
    rt: routeQuery,
    st: stationQuery,
    ind: industryQuery,
    ha: incomeQuery,
    ne: employNumQuery,
    es: establishQuery,
    so: hasSo ? 'so=50' : undefined,
    tp: jobOfferKindQuery,
    bf: defaultParams?.bf ? `bf=${defaultParams.bf}` : undefined,
    mpsc_sid: defaultParams?.mpsc_sid
      ? `mpsc_sid=${defaultParams.mpsc_sid}`
      : undefined,
    oldestDayWdtno: oldestDayWdtno
      ? `oldestDayWdtno=${oldestDayWdtno}`
      : undefined,
    leftPanelType: leftPanelType ? `leftPanelType=${leftPanelType}` : undefined,
    usrclk_searchList: usrclk ? `usrclk_searchList=${usrclk}` : undefined,
  };
};

/**
 * 計測用クエリを生成
 * @param searchType
 * @param isLogin
 * @param usrclkSearchCondition
 * @returns 'PC-logoutJobSearchList_searchConditionArea_searchButton'
 */
export const createUsrclkSearchQuery = (
  searchType: SearchType,
  isLogin: boolean,
  usrclkSearchCondition: string
) => {
  const isModal =
    searchType !== SEARCH_TYPE.SEARCH_BUTTON &&
    searchType !== SEARCH_TYPE.SEARCH_BUTTON_FLOAT;
  const loginStatus = isLogin ? LOGIN_STATUS.login : LOGIN_STATUS.logout;
  const modalSearch = isModal ? '_kyujinSearchButton' : '';
  const searchArea = `_searchConditionArea_${searchType}${modalSearch}`;

  return loginStatus + searchArea + usrclkSearchCondition;
};

/**
 * 生成したクエリのオブジェクトからクエリを生成
 * @param queryObject クエリのオブジェクト
 * @returns 'oc=02L&op=1&ne=1&es=1'
 */
export const createQueryUrl = (queryObject: SearchConditionParams) => {
  const params: string[] = [];

  const treatmentQueryObject = removeUndefinedFromObject(queryObject);

  getKeys(treatmentQueryObject).forEach((key) =>
    params.push(treatmentQueryObject[key])
  );

  return params;
};

/**
 * 生成したクエリなどを元に遷移先URLを生成
 * @param searchConditionQuery
 * @returns 遷移先URL
 */
export const createUrl = (searchConditionQuery: string[]) => {
  const url = DODA_SITE_URL.jobSearchList.concat(
    '?',
    searchConditionQuery.join('&')
  );

  return url;
};

/**
 * 計測用URLに渡す文字列を生成
 * @param selectedSearchCondition
 * @returns -ocL-ocM
 */
export const createUsrclkSearchConditionText = (
  selectedSearchCondition: SelectedCurrentConditionType
): string => {
  // 計測用urlに渡す文字列とその条件が選択されたかどうかの配列を生成
  const conditionQueryList = createUsrclkConditionQuery(
    selectedSearchCondition
  );

  return conditionQueryList
    .filter(({ isSelected }) => isSelected)
    .map(({ query }) => query)
    .reduce((a, b) => a + b, '');
};
